import React from 'react'
import PropTypes from 'prop-types'

const BASE_CLASSES = [
  'tw:inline-flex',
  'tw:items-center',
  'tw:justify-center',
  'tw:align-middle',
  'tw:cursor-pointer',
  'tw:select-none',
  'tw:leading-normal',
  'tw:text-sm',
  'tw:rounded-md!',
  'tw:py-2',
  'tw:px-3',
  // Pinned: the padding alone lands a pixel short of the DS control height.
  'tw:h-10!',
  'tw:font-normal',
  'tw:border',
  'tw:border-solid',
  'tw:transition-colors',
  'tw:disabled:opacity-50',
  'tw:disabled:cursor-not-allowed'
].join(' ')

const VARIANTS = {
  'primary': [
    'tw:bg-ds-primary',
    'tw:border-ds-primary',
    'tw:text-white',
    'tw:hover:bg-ds-primary-hover',
    'tw:hover:border-ds-primary-hover',
    'tw:focus-visible:bg-ds-primary-hover',
    'tw:focus-visible:border-ds-primary-hover',
    'tw:active:bg-ds-primary-pressed',
    'tw:active:border-ds-primary-pressed'
  ].join(' '),
  'outline': [
    'tw:bg-white',
    'tw:border-ds-primary',
    'tw:text-ds-primary',
    'tw:hover:border-ds-primary-hover',
    'tw:hover:text-ds-primary-hover',
    'tw:focus-visible:border-ds-primary-hover',
    'tw:focus-visible:text-ds-primary-hover',
    'tw:active:border-ds-primary-pressed',
    'tw:active:text-ds-primary-pressed'
  ].join(' '),
  'neutral': [
    'tw:bg-white',
    'tw:border-gray-300',
    'tw:text-ds-neutral-highest',
    'tw:hover:bg-gray-100',
    'tw:focus-visible:bg-gray-100',
    'tw:active:bg-ds-neutral-lowest',
    'tw:active:border-gray-300'
  ].join(' '),
  'warning': [
    'tw:bg-ds-progress',
    'tw:border-ds-progress',
    'tw:text-white',
    'tw:hover:bg-ds-progress-hover',
    'tw:hover:border-ds-progress-hover',
    'tw:focus-visible:bg-ds-progress-hover',
    'tw:focus-visible:border-ds-progress-hover',
    'tw:active:bg-ds-progress-pressed',
    'tw:active:border-ds-progress-pressed'
  ].join(' '),
  'destructive': [
    'tw:bg-ds-destructive',
    'tw:border-ds-destructive',
    'tw:text-white',
    'tw:hover:bg-ds-destructive-hover',
    'tw:hover:border-ds-destructive-hover',
    'tw:focus-visible:bg-ds-destructive-hover',
    'tw:focus-visible:border-ds-destructive-hover',
    'tw:active:bg-ds-destructive-pressed',
    'tw:active:border-ds-destructive-pressed'
  ].join(' '),
  'outline-destructive': [
    'tw:bg-white',
    'tw:border-ds-destructive-outline',
    'tw:text-ds-destructive-outline',
    'tw:hover:border-ds-destructive',
    'tw:focus-visible:border-ds-destructive',
    'tw:active:border-ds-destructive-hover'
  ].join(' '),
  'danger': [
    'tw:bg-ds-destructive',
    'tw:border-ds-destructive',
    'tw:text-white',
    'tw:hover:bg-ds-destructive-hover',
    'tw:hover:border-ds-destructive-hover',
    'tw:focus-visible:bg-ds-destructive-hover',
    'tw:focus-visible:border-ds-destructive-hover',
    'tw:active:bg-ds-destructive-hover',
    'tw:active:border-ds-destructive-hover'
  ].join(' '),
  'dark': [
    'tw:bg-ds-neutral-highest',
    'tw:border-ds-neutral-highest',
    'tw:text-white',
    'tw:focus-visible:outline-2',
    'tw:focus-visible:outline-ds-primary'
  ].join(' ')
}

const Button = ({
  children,
  variant = 'primary',
  icon = null,
  iconNode = null,
  iconPosition = 'left',
  type = 'button',
  disabled = false,
  className = '',
  href = null,
  onClick = null,
  ...rest
}) => {
  // `tw:no-underline!` beats Bootstrap reboot's out-of-layer `a { text-decoration: underline }`.
  const anchorReset = href ? ' tw:no-underline!' : ''
  const classes = `${BASE_CLASSES} ${VARIANTS[variant]}${anchorReset} ${className}`.trim()
  const iconMargin = iconPosition === 'right' ? 'tw:ml-2' : 'tw:mr-2'
  let iconElement = null
  if (iconNode)
    iconElement = <span className={`tw:inline-flex tw:items-center ${iconMargin}`}>{iconNode}</span>
  else if (icon)
    iconElement = <i className={`fas fa-${icon} ${iconMargin}`} />

  const content = (
    <>
      {iconPosition === 'left' ? iconElement : null}
      {children}
      {iconPosition === 'right' ? iconElement : null}
    </>
  )

  // `<a>` ignores `disabled`, so a disabled link falls through to the <button> branch.
  if (href && !disabled)
    return <a className={classes} href={href} onClick={onClick} {...rest}>{content}</a>

  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      className={classes}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {content}
    </button>
  )
}

Button.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.string,
  iconNode: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit']),
  variant: PropTypes.oneOf([
    'primary',
    'outline',
    'outline-destructive',
    'neutral',
    'warning',
    'destructive',
    'danger',
    'dark'
  ])
}

Button.defaultProps = {
  className: '',
  disabled: false,
  href: null,
  icon: null,
  iconNode: null,
  iconPosition: 'left',
  onClick: null,
  type: 'button',
  variant: 'primary'
}

export default Button
